/* Query hooks for the catalogue and for usage — STORY-031. */

import {
  type BillingPeriodCode,
  plansResponseSchema,
  startCheckoutResponseSchema,
  subscriptionHistoryResponseSchema,
  subscriptionResponseSchema,
  usageResponseSchema,
} from '@eventuley/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { request } from '../../../shared/api/client.js';

export const plansKey = ['plans'] as const;

/**
 * The catalogue.
 *
 * Cached for the session: four rows of reference data that change when a
 * migration ships, not while somebody is reading the page.
 */
export function usePlans() {
  return useQuery({
    queryKey: plansKey,
    queryFn: () => request('/plans', { schema: plansResponseSchema }),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export const usageKey = (organisationId: string) =>
  ['organisations', organisationId, 'usage'] as const;

export function useUsage(organisationId: string | undefined) {
  return useQuery({
    queryKey: usageKey(organisationId ?? ''),
    queryFn: () =>
      request(`/organisations/${organisationId}/usage`, { schema: usageResponseSchema }),
    /* Nothing to ask for when no workspace is named — the plans page is a sales
     * page as much as an account screen, and signed out there is no usage. */
    enabled: Boolean(organisationId),
    retry: false,
  });
}

/**
 * Opens a hosted checkout and hands back where to send the browser — STORY-032.
 *
 * A mutation rather than a query because it CREATES something at the provider:
 * a Checkout Session is a real object with a real idempotency key, and running
 * it on render — which is what a query would do — would open one every time
 * somebody looked at the page.
 *
 * The redirect is deliberately left to the caller. Navigating inside a mutation
 * makes the hook untestable without a jsdom location stub, and the caller is
 * the one that knows whether it still wants to.
 */
export function useStartCheckout(organisationId: string | undefined) {
  return useMutation({
    mutationFn: (input: { planCode: string; billingPeriod: BillingPeriodCode }) =>
      request(`/organisations/${organisationId}/subscription/checkout`, {
        method: 'POST',
        body: input,
        schema: startCheckoutResponseSchema,
      }),
  });
}

export const subscriptionKey = (organisationId: string) =>
  ['organisations', organisationId, 'subscription'] as const;

/**
 * What this workspace is paying — STORY-032.
 *
 * Readable by any Member: what plan the workspace is on shapes what every
 * Member can do, and a limit whose reason nobody can see reads as a bug.
 */
export function useSubscription(organisationId: string | undefined) {
  return useQuery({
    queryKey: subscriptionKey(organisationId ?? ''),
    queryFn: () =>
      request(`/organisations/${organisationId}/subscription`, {
        schema: subscriptionResponseSchema,
      }),
    enabled: Boolean(organisationId),
    retry: false,
  });
}

/** The billing trail — STORY-032, STORY-034. Owner-only; a Member gets 403. */
export function useSubscriptionHistory(organisationId: string | undefined) {
  return useQuery({
    queryKey: [...subscriptionKey(organisationId ?? ''), 'history'],
    queryFn: () =>
      request(`/organisations/${organisationId}/subscription/history`, {
        schema: subscriptionHistoryResponseSchema,
      }),
    enabled: Boolean(organisationId),
    retry: false,
  });
}

/**
 * Stops the subscription renewing, or starts it again — STORY-034.
 *
 * Invalidates the subscription on success, but does NOT assume the new state:
 * the server writes nothing either, and the change lands when the provider's
 * webhook confirms it. A refetch may still show the old value for a moment,
 * and that is the honest thing to show — the alternative is claiming a
 * cancellation that has not happened.
 */
export function useSetCancellation(organisationId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (cancelAtPeriodEnd: boolean) =>
      request(`/organisations/${organisationId}/subscription/cancellation`, {
        method: 'POST',
        body: { cancelAtPeriodEnd },
        schema: z.object({ requested: z.literal(true) }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: subscriptionKey(organisationId ?? '') });
    },
  });
}
